import { isNil } from 'lodash';

import { roundAtSecondDecimal } from './numbers';
import { LeasePriceHistoryDTO, LeaseVariousOperationDTO } from './types/dto-app';
import {
  LeaseAmountUpdateStatus,
  LeasePriceHistory,
  LeasePriceHistoryChargeAmountsDetails,
  LeasePriceHistoryDetail,
  LeasePriceHistoryType,
  LeaseVariousOperation,
  LeaseVariousOperationType,
} from './types/model-app';

type AnyLeasePriceHistory = LeasePriceHistory | LeasePriceHistoryDTO;

const isChargeIndexationBaseSource = (leasePriceHistory: AnyLeasePriceHistory): boolean =>
  leasePriceHistory.type === LeasePriceHistoryType.INITIAL_PRICE ||
  leasePriceHistory.status === LeaseAmountUpdateStatus.APPLIED;

const compareByOldestFirst = (first: AnyLeasePriceHistory, second: AnyLeasePriceHistory): number => {
  const dateComparison = (first.applicationDate ?? '').localeCompare(second.applicationDate ?? '');
  if (dateComparison !== 0) {
    return dateComparison;
  }
  const isInitialPrice = (leasePriceHistory: AnyLeasePriceHistory) =>
    leasePriceHistory.type === LeasePriceHistoryType.INITIAL_PRICE ? 0 : 1;
  return isInitialPrice(first) - isInitialPrice(second);
};

/**
 * A fixed charge is indexed from the amount it had when it first appeared on the lease, against an
 * index base anchored on the lease start. `LeaseVariousOperation.amount` cannot serve as that base:
 * every applied indexation overwrites it, so re-applying the whole since-lease-start ratio to it
 * compounds the increase year after year.
 *
 * The INITIAL_PRICE records that amount for a charge encoded with the lease. A charge added later
 * is absent from it, and the oldest applied indexation mentioning the charge is then the earliest
 * record of its pre-indexation amount. Drafts are ignored: they carry the amount as it stands now,
 * which is the very value the base has to be independent of.
 */
export const getChargeIndexationBaseAmountsByLvoId = (
  leasePriceHistories: AnyLeasePriceHistory[]
): Map<string, number> =>
  leasePriceHistories
    .filter(isChargeIndexationBaseSource)
    .sort(compareByOldestFirst)
    .reduce<Map<string, number>>(
      (acc, leasePriceHistory) =>
        (leasePriceHistory.amountDetails ?? []).reduce(
          (detailAcc, amountDetail) =>
            (amountDetail?.chargesAmountsDetails ?? []).reduce((chargeAcc, chargeAmountsDetails) => {
              if (isNil(chargeAmountsDetails) || chargeAcc.has(chargeAmountsDetails.leaseVariousOperationId)) {
                return chargeAcc;
              }
              chargeAcc.set(chargeAmountsDetails.leaseVariousOperationId, chargeAmountsDetails.previousMonthlyAmount);
              return chargeAcc;
            }, detailAcc),
          acc
        ),
      new Map<string, number>()
    );

/**
 * A base of 0 means the initial charge price was never encoded, not that the charge started free:
 * indexing 0 would silently drop the charge to 0 on the next anniversary.
 */
export const getChargeIndexationBaseAmount = (
  chargeIndexationBaseAmounts: Map<string, number>,
  leaseVariousOperationId: string,
  currentMonthlyAmount: number
): number => {
  const baseAmount = chargeIndexationBaseAmounts.get(leaseVariousOperationId);
  return isNil(baseAmount) || baseAmount === 0 ? currentMonthlyAmount : baseAmount;
};

/**
 * The amount a fixed charge is indexed from: its corrected base, else the amount it first appeared with, else today's.
 */
export const getFixedChargeStartAmount = (
  leaseVariousOperation: Pick<LeaseVariousOperation, 'id' | 'amount' | 'indexationBaseAmount'>,
  chargeIndexationBaseAmounts: Map<string, number>
): number => {
  const { indexationBaseAmount } = leaseVariousOperation;
  return isNil(indexationBaseAmount) || indexationBaseAmount <= 0
    ? getChargeIndexationBaseAmount(chargeIndexationBaseAmounts, leaseVariousOperation.id, leaseVariousOperation.amount)
    : indexationBaseAmount;
};

export interface ChargeIndexationBase {
  /** Null when neither the line nor its unit detail recorded one: each reader keeps its own default. */
  baseIndex: number | null;
  baseAmount: number;
  /** False when the run left the line unchanged (frozen, or based in or after the new index's month). */
  isIndexed: boolean;
}

/** Records written before REN-9014 carry no flag: their lines read as indexed. */
export const isChargeLineIndexed = (
  chargeAmountsDetail?: Pick<LeasePriceHistoryChargeAmountsDetails, 'indexed'> | null
): boolean => chargeAmountsDetail?.indexed !== false;

export const isWaitingIndexationDraft = (
  leasePriceHistory?: Pick<LeasePriceHistory, 'type' | 'status'> | null
): boolean =>
  leasePriceHistory?.type === LeasePriceHistoryType.INDEXATION &&
  leasePriceHistory.status === LeaseAmountUpdateStatus.DRAFT;

/**
 * Records written before REN-9014 carry no base per charge: the unit's base index and derived base amount apply.
 * Records without the `indexed` flag are indexed.
 */
export const getChargeIndexationBase = (
  amountDetail: Pick<LeasePriceHistoryDetail, 'indexationBaseIndex'>,
  chargeAmountsDetail: Pick<
    LeasePriceHistoryChargeAmountsDetails,
    'leaseVariousOperationId' | 'previousMonthlyAmount' | 'indexationBaseIndex' | 'indexationBaseAmount' | 'indexed'
  >,
  chargeIndexationBaseAmounts: Map<string, number>
): ChargeIndexationBase => ({
  baseIndex: chargeAmountsDetail.indexationBaseIndex ?? amountDetail.indexationBaseIndex ?? null,
  baseAmount:
    chargeAmountsDetail.indexationBaseAmount ??
    getChargeIndexationBaseAmount(
      chargeIndexationBaseAmounts,
      chargeAmountsDetail.leaseVariousOperationId,
      chargeAmountsDetail.previousMonthlyAmount
    ),
  isIndexed: isChargeLineIndexed(chargeAmountsDetail),
});

export interface MonthlyChargesSummary {
  previousMonthlyCharges: number;
  newMonthlyCharges: number;
}

export const sumPreviousMonthlyChargesFromDetail = (detail: LeasePriceHistoryDetail | undefined): number =>
  (detail?.chargesAmountsDetails ?? []).reduce((sum, d) => sum + d.previousMonthlyAmount, 0);

export const sumChargeIndexationBaseAmountsFromDetail = (
  detail: LeasePriceHistoryDetail | undefined,
  chargeIndexationBaseAmounts: Map<string, number>
): number =>
  roundAtSecondDecimal(
    (detail?.chargesAmountsDetails ?? []).reduce(
      (sum, chargeAmountsDetails) =>
        sum +
        getChargeIndexationBaseAmount(
          chargeIndexationBaseAmounts,
          chargeAmountsDetails.leaseVariousOperationId,
          chargeAmountsDetails.previousMonthlyAmount
        ),
      0
    )
  );

export const sumNewMonthlyChargesFromDetail = (detail: LeasePriceHistoryDetail | undefined): number =>
  (detail?.chargesAmountsDetails ?? []).reduce((sum, d) => sum + d.newMonthlyAmount, 0);

export const getMonthlyChargesSummaryFromAmountDetails = (
  amountDetails: LeasePriceHistoryDetail[]
): MonthlyChargesSummary => {
  const { previousMonthlyCharges, newMonthlyCharges } = amountDetails.reduce<MonthlyChargesSummary>(
    (acc, amountDetail) => ({
      previousMonthlyCharges: acc.previousMonthlyCharges + sumPreviousMonthlyChargesFromDetail(amountDetail),
      newMonthlyCharges: acc.newMonthlyCharges + sumNewMonthlyChargesFromDetail(amountDetail),
    }),
    { previousMonthlyCharges: 0, newMonthlyCharges: 0 }
  );
  return {
    previousMonthlyCharges: roundAtSecondDecimal(previousMonthlyCharges),
    newMonthlyCharges: roundAtSecondDecimal(newMonthlyCharges),
  };
};

export const getTotalMonthlyChargesFromLvos = (lvos: (LeaseVariousOperation | null | undefined)[]): number =>
  roundAtSecondDecimal(
    lvos.reduce((sum, lvo) => {
      if (
        isNil(lvo) ||
        (lvo.operationType !== LeaseVariousOperationType.FIXED_CHARGE &&
          lvo.operationType !== LeaseVariousOperationType.PROVISIONED_CHARGE)
      ) {
        return sum;
      }
      return sum + lvo.amount;
    }, 0)
  );

export const getChargesAmountsDetailsFromLeaseVariousOperation = (
  leaseVariousOperations: (LeaseVariousOperation | LeaseVariousOperationDTO)[],
  unitId: string
): Omit<LeasePriceHistoryChargeAmountsDetails, '__typename'>[] =>
  leaseVariousOperations.reduce<Omit<LeasePriceHistoryChargeAmountsDetails, '__typename'>[]>((acc, lvo) => {
    if (
      lvo.unitId === unitId &&
      [LeaseVariousOperationType.FIXED_CHARGE, LeaseVariousOperationType.PROVISIONED_CHARGE].includes(lvo.operationType)
    ) {
      acc.push({
        leaseVariousOperationId: lvo.id,
        newMonthlyAmount: lvo.amount,
        previousMonthlyAmount: lvo.amount,
      });
    }
    return acc;
  }, []);
